import { 
  FaGlobe, FaBook, FaHandHoldingHeart, FaLeaf, FaPaw, 
  FaPalette, FaHeartbeat, FaUsers, FaChild, FaMicroscope,
  FaBalanceScale, FaHandsHelping, FaLaptopCode, FaRobot,
  FaRainbow, FaChurch, FaGraduationCap, FaFootballBall,
  FaHeart, FaHandsWash
} from 'react-icons/fa';
import React from 'react';

// Combine IRS categories with existing categories
export const CAUSE_CATEGORIES = {
  // Primary IRS Categories
  CHARITABLE: {
    value: 'charitable',
    label: 'Charitable Programs',
    icon: <FaHandHoldingHeart />,
    subcategories: {
      HUMANITARIAN: {
        value: 'humanitarian',
        label: 'Humanitarian Aid',
        icon: <FaHandsHelping />
      },
      DISASTER_RELIEF: {
        value: 'disaster',
        label: 'Disaster Relief',
        icon: <FaHandsHelping />
      },
      POVERTY_RELIEF: {
        value: 'poverty_relief',
        label: 'Poverty Relief',
        icon: <FaHeart />
      }
    }
  },
  EDUCATIONAL: {
    value: 'education',
    label: 'Education',
    icon: <FaBook />,
    subcategories: {
      YOUTH: {
        value: 'youth',
        label: 'Youth Programs',
        icon: <FaChild />
      },
      TECH_EDUCATION: {
        value: 'tech',
        label: 'Technology Education',
        icon: <FaLaptopCode />
      }
    }
  },
  SCIENTIFIC: {
    value: 'scientific',
    label: 'Scientific Programs',
    icon: <FaMicroscope />,
    subcategories: {
      RESEARCH: {
        value: 'research',
        label: 'Research & Innovation',
        icon: <FaMicroscope />
      },
      AI_ETHICS: {
        value: 'ai',
        label: 'AI Ethics',
        icon: <FaRobot />
      }
    }
  },
  ENVIRONMENTAL: {
    value: 'environment',
    label: 'Environmental',
    icon: <FaLeaf />
  },
  ANIMAL_WELFARE: {
    value: 'animals',
    label: 'Animal Welfare',
    icon: <FaPaw />
  },
  ARTS_CULTURE: {
    value: 'arts',
    label: 'Arts & Culture',
    icon: <FaPalette />
  },
  HEALTH: {
    value: 'health',
    label: 'Health & Wellness',
    icon: <FaHeartbeat />
  },
  COMMUNITY: {
    value: 'community',
    label: 'Community Development',
    icon: <FaUsers />
  },
  SOCIAL_JUSTICE: {
    value: 'social',
    label: 'Social Justice',
    icon: <FaBalanceScale />,
    subcategories: {
      LGBTQ: {
        value: 'lgbtq',
        label: 'LGBTQ+',
        icon: <FaRainbow />
      }
    }
  },
  RELIGIOUS: {
    value: 'religious',
    label: 'Religious',
    icon: <FaChurch />
  },
  AMATEUR_SPORTS: {
    value: 'amateur_sports',
    label: 'Amateur Sports',
    icon: <FaFootballBall />
  },
  PUBLIC_SAFETY: {
    value: 'public_safety',
    label: 'Public Safety',
    icon: <FaHandsWash />
  }
};

// Helper function to get all categories as a flat array (for backwards compatibility)
export const getCategoriesArray = () => {
  const flatCategories = [
    { id: 'all', name: 'All Causes', icon: <FaGlobe /> }
  ];
  
  Object.entries(CAUSE_CATEGORIES).forEach(([key, category]) => {
    flatCategories.push({
      id: category.value,
      name: category.label,
      icon: category.icon
    });
    
    if (category.subcategories) {
      Object.entries(category.subcategories).forEach(([subKey, subCategory]) => {
        flatCategories.push({
          id: subCategory.value,
          name: subCategory.label,
          icon: subCategory.icon,
          parentId: category.value
        });
      });
    }
  });
  
  return flatCategories;
};

// For backwards compatibility
export const categories = getCategoriesArray();

// Add comprehensive NTEE codes
export const NTEE_CODES = {
  // Arts, Culture & Humanities
  'A': 'Arts, Culture & Humanities',
  'A01': 'Arts & Culture Alliances',
  'A20': 'Arts Education',
  'A23': 'Cultural & Ethnic Awareness',
  'A30': 'Media & Communications',
  'A40': 'Visual Arts',
  'A50': 'Museums',
  'A60': 'Performing Arts',
  'A70': 'Humanities',

  // Education
  'B': 'Education',
  'B01': 'Education Alliances',
  'B20': 'Elementary & Secondary Education',
  'B21': 'Special Education',
  'B30': 'Adult Education',
  'B40': 'Higher Education',
  'B50': 'Libraries',
  'B60': 'Student Services',
  'B70': 'Educational Services',
  'B80': 'Educational Support',
  'B90': 'Educational Research',

  // Environment
  'C': 'Environment',
  'C01': 'Environmental Alliances',
  'C20': 'Pollution Abatement',
  'C27': 'Recycling',
  'C30': 'Natural Resources Conservation',
  'C32': 'Water Resources',
  'C34': 'Land Resources',
  'C35': 'Energy Resources',
  'C36': 'Forest Conservation',
  'C40': 'Botanical & Horticultural Programs',
  'C50': 'Environmental Education',

  // Health
  'E': 'Health',
  'E20': 'Hospitals',
  'E30': 'Mental Health',
  'E31': 'Psychiatric Care',
  'E32': 'Substance Abuse Treatment',
  'E40': 'Disease Research',
  'E50': 'Public Health',
  'E60': 'Health Support Services',
  'E70': 'Public Health Systems',

  // Research & Public Policy
  'V': 'Research & Public Policy',
  'V01': 'Research Institutes',
  'V20': 'Science & Technology Research',
  'V30': 'Public Policy Research',
  'V31': 'Black-led Research',
  'V32': 'Democracy & Civic Engagement',
  'V33': 'Social Science Research',

  // Social Justice & Advocacy
  'R': 'Social Justice & Advocacy',
  'R01': 'Civil Rights',
  'R20': 'Advocacy Organizations',
  'R22': 'Minority Rights',
  'R23': 'Disability Rights',
  'R24': 'Women\'s Rights',
  'R25': 'LGBTQ+ Rights',
  'R26': 'Immigration Rights',
  'R30': 'Intergroup & Race Relations',

  // Youth Development
  'O': 'Youth Development',
  'O20': 'Youth Centers & Clubs',
  'O30': 'Adult & Child Matching Programs',
  'O40': 'Scouting Organizations',
  'O50': 'Youth Development Programs',

  // Effective Altruism
  'W': 'Effective Altruism',
  'W20': 'Evidence-Based Giving',
  'W30': 'Global Development',
  'W40': 'Animal Welfare',
  'W50': 'Future of Humanity',
  'W60': 'AI Safety & Ethics'
};

// Add CAUSE_STATUSES definition
export const CAUSE_STATUSES = {
  ACTIVE: {
    value: 'active',
    label: 'Active',
    color: '#4CAF50' // green
  },
  PENDING: {
    value: 'pending',
    label: 'Pending',
    color: '#FFC107' // amber
  },
  COMPLETED: {
    value: 'completed',
    label: 'Completed',
    color: '#2196F3' // blue
  },
  ON_HOLD: {
    value: 'on_hold',
    label: 'On Hold',
    color: '#FF9800' // orange
  },
  ARCHIVED: {
    value: 'archived',
    label: 'Archived',
    color: '#9E9E9E' // grey
  }
}; 